import React, { useState, useCallback, useEffect, useRef } from "react";
import { Column, Text } from "@amzn/meridian";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setScannedBarcode,
  addScannedBarcode,
  setBarcodeScanningMode,
} from "src/store/inspectionCaptureSlice";
import DuplicateBarcodeAlertModal from "./DuplicateBarcodeAlertModal";
import UnkownObjectBarcodeAlertModal from "./UnkownObjectBarcodeAlertModal";
import ManualBarcodeEntryModal from "./ManualBarcodeEntryModal";
import { barcodeScanTimingLogger, BarcodeEntryMethod } from "src/services/barcodeScanTimingLogger";
import ConfirmContainerCountSheet from "./ConfirmContainerCountSheet";
import "./BarcodeFrame.scss";
import "src/components/UI/cameraOverlay.scss";
import PillButton from "src/components/UI/PillButton";

interface BarcodeFrameProps {
  onBarcodeConfirmed?: () => void;
  /** When provided, shows an "All Containers Finished" pill to end the loop. */
  onFinish?: () => void;
}

const isMobile = window.matchMedia('(pointer: coarse)').matches;

const BarcodeFrame: React.FC<BarcodeFrameProps> = ({
  onBarcodeConfirmed,
  onFinish,
}) => {
  const dispatch = useAppDispatch();

  const { scannedBarcode, scannedBarcodes } = useAppSelector(
    (state) => state.inspectionCapture
  );
  const { currentIterationIndex } = useAppSelector((state) => state.workflow);

  // handleScan is memoized with empty deps, so the validateAndProcessBarcode it
  // captures closes over the initial-render scannedBarcodes. Read the latest
  // value through a ref instead, so duplicate detection and containerIndex are
  // correct for camera scans regardless of when the closure was created.
  const scannedBarcodesRef = useRef(scannedBarcodes);
  useEffect(() => {
    scannedBarcodesRef.current = scannedBarcodes;
  }, [scannedBarcodes]);

  const [isScanning, setIsScanning] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showUnknownObjectBarcodeAlert, setShowUnknownObjectBarcodeAlert] = useState(false);
  const [duplicateBarcode, setDuplicateBarcode] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  const dpr = window.devicePixelRatio || 1;
  const idealWidth = isMobile ? 1280 : Math.floor(window.innerWidth * dpr);
  const idealHeight = isMobile ? 720 : Math.floor(window.innerHeight * dpr);

  const VALID_CONTAINER_PREFIXES = ["HAZ", "NHZ", "UNI", "REC", "MUN", "PHA"];

  const validateAndProcessBarcode = (barcodeValue: string, entryMethod: BarcodeEntryMethod) => {
    dispatch(setScannedBarcode(barcodeValue));

    // Record the user-perceived scan time (scan-screen ready → barcode value
    // obtained) here, at the point a value is produced, tagged with how it was
    // obtained (camera vs manual) and the outcome. Doing it here — rather than
    // only on a successful confirm — ensures manual entries and duplicate /
    // unknown attempts are always timed, not silently dropped by an early
    // return. containerIndex is this container's 1-based session ordinal,
    // derived from the de-duped scannedBarcodes list (workflow.currentIteration
    // Index hasn't advanced yet at scan time); it lines up with the capture
    // logs' containerIndex for the same container.
    const currentScannedBarcodes = scannedBarcodesRef.current;
    const containerIndex = currentScannedBarcodes.length + 1;

    if (currentScannedBarcodes.includes(barcodeValue)) {
      barcodeScanTimingLogger.recordScan({ entryMethod, outcome: "duplicate", containerId: barcodeValue, containerIndex });
      setDuplicateBarcode(barcodeValue);
      setShowDuplicateAlert(true);
      return;
    }

    const barcodePrefix = barcodeValue.substring(0, 3).toUpperCase();
    if (!VALID_CONTAINER_PREFIXES.includes(barcodePrefix)) {
      barcodeScanTimingLogger.recordScan({ entryMethod, outcome: "unknown", containerId: barcodeValue, containerIndex });
      setShowUnknownObjectBarcodeAlert(true);
      return;
    }

    barcodeScanTimingLogger.recordScan({ entryMethod, outcome: "scanned", containerId: barcodeValue, containerIndex });
    handleConfirm(barcodeValue);
  };

  const handleScan = useCallback(
    (result: any) => {
      if (result && result.length > 0) {
        const barcodeValue = result[0].rawValue;

        if (/^\d+$/.test(barcodeValue)) {
          return;
        }

        validateAndProcessBarcode(barcodeValue, "camera");
      }
    },
    []
  );

  const handleError = useCallback((error: any) => {
    console.error("Camera error:", error);
  }, []);

  const handleConfirm = (barcodeValue: string) => {
    if (barcodeValue) {
      dispatch(addScannedBarcode(barcodeValue));
    }
    if (onBarcodeConfirmed) {
      onBarcodeConfirmed();
    } else {
      dispatch(setBarcodeScanningMode(false));
    }
  };

  const handleDuplicateAlertClose = () => {
    setShowDuplicateAlert(false);
    setDuplicateBarcode("");
    // The previous attempt was already recorded (outcome "duplicate"), which
    // consumed the timer. Restart it so the next scan attempt is timed fresh.
    barcodeScanTimingLogger.start();
  };

  const handleRescan = () => {
    dispatch(setScannedBarcode(null));
    setShowUnknownObjectBarcodeAlert(false);
    // Restart the timer for the retry (the "unknown" attempt consumed it).
    barcodeScanTimingLogger.start();
  };

  const handleManualBarcodeSubmit = (barcode: string) => {
    setShowManualEntry(false);
    validateAndProcessBarcode(barcode, "manual");
  };

  useEffect(() => {
    setIsScanning(true);
    // Start timing the scan when the scan screen becomes ready. A fresh
    // BarcodeFrame is mounted per container iteration (ReadBarcodeStep), so
    // each scan is timed independently.
    barcodeScanTimingLogger.start();
    return () => {
      barcodeScanTimingLogger.cancel();
    };
  }, []);

  return (
    <Column className="barcode-frame-viewport pwa-fullscreen-viewport" height="100vh">
      {isScanning && (
        <Scanner
          onScan={handleScan}
          onError={handleError}
          formats={['code_128', 'upc_a', 'ean_13']}
          components={{finder: false}}
          allowMultiple={false}
          constraints={{
            facingMode: "environment",
            width: { ideal: idealWidth },
            height: { ideal: idealHeight },
          }}
          styles={{
            container: {
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            },
            video: {
              width: "100%",
              height: "100%",
              objectFit: "cover",
            },
          }}
        />
      )}

      <div className="barcode-guidance-pill">
        <Column className="camera-pill-bg" width="300px" alignmentHorizontal="center" spacingInset="300">
          <Text alignment="center" color="inverted">Scan the barcode on the top of the container lid</Text>
        </Column>
      </div>

      <div
        style={{
          position: "absolute",
          top: "52%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxWidth: "700px",
          height: "40%",
          border: "3px solid #00A650",
          borderRadius: "12px",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <DuplicateBarcodeAlertModal
        open={showDuplicateAlert}
        barcode={duplicateBarcode}
        onClose={handleDuplicateAlertClose}
      />

      <UnkownObjectBarcodeAlertModal
        open={showUnknownObjectBarcodeAlert}
        barcode={scannedBarcode || ""}
        onRescan={handleRescan}
      />

      {/* Bottom gradient scrim keeps both pill buttons readable over a bright
          camera feed, scoped to the bottom band rather than the full overlay. */}
      <div className="camera-bottom-scrim" />

      {/* One flex column spanning from just below the frame down to the bottom.
          `space-between` keeps the "Having Trouble" pill dynamically centered
          between the guidance text and the finish button on any screen size.
          Uses the base `camera-overlay` class for z-index/width and the PWA
          `position: fixed` behavior; the safe-area padding keeps the finish
          button clear of the iOS home indicator. */}
      <div
        className="camera-overlay"
        style={{
          top: "75%",
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
        }}
      >
        <Column className="camera-pill-bg" width="300px" alignmentHorizontal="center" spacingInset="300">
          <Text color="inverted" type="h100" alignment="center">
            {isScanning ? "Position barcode within frame" : "Processing..."}
          </Text>
        </Column>

        <PillButton text="Having Trouble? Try Manual Entry" color="glass" onClick={() => setShowManualEntry(true)} />

        {onFinish && (
          <PillButton
            text="ALL CONTAINERS FINISHED"
            color="primary"
            disabled={currentIterationIndex === 0}
            onClick={() => setShowFinishConfirm(true)}
          />
        )}
      </div>

      <ConfirmContainerCountSheet
        open={showFinishConfirm}
        count={currentIterationIndex}
        onConfirm={() => {
          setShowFinishConfirm(false);
          onFinish?.();
        }}
        onCancel={() => setShowFinishConfirm(false)}
      />

      <ManualBarcodeEntryModal
        open={showManualEntry}
        onSubmit={handleManualBarcodeSubmit}
        onClose={() => {
          setShowManualEntry(false);
        }}
      />
    </Column>
  );
};

export default BarcodeFrame;
